// Role-Based Access Control (RBAC) Types

// Role names as defined in the database
export type RoleName = 'superuser' | 'administrator' | 'customer_manager' | 'user';

// Section names for granular permissions
export type SectionName = 'contacts' | 'products' | 'devices' | 'credentials' | 'billing';

// Role hierarchy levels (lower = higher privilege)
export const ROLE_HIERARCHY: Record<RoleName, number> = {
  superuser: 1,
  administrator: 2,
  customer_manager: 3,
  user: 4,
};

// Portal user linked to Clerk
export interface PortalUser {
  id: string;
  clerk_user_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Role definition
export interface Role {
  id: string;
  name: RoleName;
  display_name: string;
  hierarchy_level: number;
  created_at: string;
}

// User role assignment
export interface UserRole {
  id: string;
  user_id: string;
  role_id: string;
  created_at: string;
}

// Customer access assignment
export interface UserCustomerAccess {
  id: string;
  user_id: string;
  customer_id: string;
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
}

// Section-level permissions
export interface UserSectionPermission {
  id: string;
  user_customer_access_id: string;
  section_name: SectionName;
  can_view: boolean;
  can_edit: boolean;
  created_at: string;
  updated_at: string;
}

// Extended types with relations
export interface UserRoleWithDetails extends UserRole {
  role: Role;
}

export interface UserCustomerAccessWithDetails extends UserCustomerAccess {
  customer_name: string;
  section_permissions: UserSectionPermission[];
}

export interface PortalUserWithRoles extends PortalUser {
  roles: Role[];
  highest_role_level: number;
}

export interface PortalUserFull extends PortalUserWithRoles {
  customer_access: UserCustomerAccessWithDetails[];
}

// Form types
export type PortalUserInsert = Omit<PortalUser, 'id' | 'created_at' | 'updated_at'>;
export type PortalUserUpdate = Partial<Omit<PortalUserInsert, 'clerk_user_id'>>;

export type UserCustomerAccessInsert = Omit<UserCustomerAccess, 'id' | 'created_at' | 'updated_at'>;
export type UserCustomerAccessUpdate = Partial<Omit<UserCustomerAccessInsert, 'user_id' | 'customer_id'>>;

export type UserSectionPermissionInsert = Omit<UserSectionPermission, 'id' | 'created_at' | 'updated_at'>;
export type UserSectionPermissionUpdate = Partial<Omit<UserSectionPermissionInsert, 'user_customer_access_id' | 'section_name'>>;

// Effective permissions for the current user context
export interface EffectivePermissions {
  isSuperuser: boolean;
  isAdministrator: boolean;
  isCustomerManager: boolean;
  isUser: boolean;
  highestRole: RoleName | null;
  highestRoleLevel: number;

  // Customer-specific permissions (only relevant for non-superusers)
  hasAccessToCustomer: (customerId: string) => boolean;
  canViewCustomer: (customerId: string) => boolean;
  canEditCustomer: (customerId: string) => boolean;
  canDeleteCustomer: (customerId: string) => boolean;

  // Section-specific permissions
  canViewSection: (customerId: string, section: SectionName) => boolean;
  canEditSection: (customerId: string, section: SectionName) => boolean;

  // Get list of accessible customer IDs
  accessibleCustomerIds: string[];
}

// Permission context state
export interface PermissionContextState {
  portalUser: PortalUserFull | null;
  loading: boolean;
  error: string | null;
  permissions: EffectivePermissions | null;
  refreshPermissions: () => Promise<void>;
}

// Role display info for UI
export const ROLE_DISPLAY: Record<RoleName, { label: string; description: string; color: string }> = {
  superuser: {
    label: 'Superuser',
    description: 'Full access to all companies and portal management',
    color: 'purple',
  },
  administrator: {
    label: 'Administrator',
    description: 'Full access to assigned companies',
    color: 'blue',
  },
  customer_manager: {
    label: 'Customer Manager',
    description: 'Limited management of assigned customers',
    color: 'green',
  },
  user: {
    label: 'User',
    description: 'Read-only access to assigned customers',
    color: 'slate',
  },
};

// Section display info for UI
export const SECTION_DISPLAY: Record<SectionName, { label: string; icon: string }> = {
  contacts: { label: 'Contacts', icon: 'Users' },
  products: { label: 'Products', icon: 'Package' },
  devices: { label: 'Devices', icon: 'Monitor' },
  credentials: { label: 'Credentials', icon: 'Key' },
  billing: { label: 'Billing', icon: 'DollarSign' },
};

// All section names for iteration
export const ALL_SECTIONS: SectionName[] = ['contacts', 'products', 'devices', 'credentials', 'billing'];
